import React, { ForwardedRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import Chart from '..';
import { ChartOption, ChartRef, IEChartsTooltipParams, PercentStackBarChartProps } from '../_config/_interface';
import TextBubbles from '@home/components/TextBubbles';
import { useBaseOptions } from '../chartConfigs/useBaseOptions';
import { setDataLabel, setXLabelAlign } from '../chartConfigs/plugs';
import { orderBy } from 'lodash-es';
import { buildAxisPercentBarSeries } from '../utils/axisPercent';
import { ITooltipRecord } from '../chartConfigs/types';
import { formatNumberByComma, getNullTextOfFilter } from '@/pages/datalab/utils/utils';
import { generateTooltipGrid } from '@customComp/Chart/chartConfigs/tooltip';
import { changeLegendLocation } from '@customComp/Chart/chartConfigs/legend/legendUtils';
import { LIGHT_COLOR_ARR } from '../_config/_constant';
import {
  buildLegendKey,
  buildLegendPaletteIndexMap,
  findRgbaForLegend,
} from '@/pages/datalab/utils/chartLegend';
import { setTooltipMaxSize } from '@customComp/Chart/chartConfigs/tooltip/config';

const PercentStackBarChartInner: React.ForwardRefRenderFunction<ChartRef, PercentStackBarChartProps> = (props, ref: ForwardedRef<ChartRef>) => {
  const { chartData, metrics, dimensions, chartConfig } = props;
  const { seriesData, xAxisData } = chartData || {};
  const { yAxisName, tooltipSetting, xLabel, } = chartConfig || {};
  const isOnlyOneDimension = dimensions?.length === 1;
  const hasYAxisName = Boolean(yAxisName);
  const { formatMessage } = useIntl();
  const chartRef = useRef<ChartRef>(null);
  const chart = chartRef?.current?.cRef;
  // 获取实例
  const instance = () => chartRef?.current?.instance();
  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance
  }));

  const percentSeries = useMemo(
    () => buildAxisPercentBarSeries(seriesData, xAxisData),
    [seriesData, xAxisData],
  );

  const legendData = useMemo(
    () => seriesData?.map(item => item?.name) ?? [],
    [seriesData],
  );
  const seriesColors = useMemo(() => {
    if (!seriesData?.length) {
      return LIGHT_COLOR_ARR;
    }
    const getLegendKey = (item: (typeof seriesData)[number]): string =>
      buildLegendKey({
        controlName: String(
          (item as { control_name_cn?: string; }).control_name_cn ?? '',
        ),
        seriesDataName: String(item?.name ?? item?.metric ?? ''),
      });
    const legendKeysInOrder: string[] = [];
    const legendKeySeen = new Set<string>();
    seriesData.forEach(item => {
      const legendKey = getLegendKey(item);
      if (!legendKey || legendKeySeen.has(legendKey)) {
        return;
      }
      legendKeySeen.add(legendKey);
      legendKeysInOrder.push(legendKey);
    });
    const legendKeyToPaletteIndex = buildLegendPaletteIndexMap(legendKeysInOrder);
    return seriesData.map((item, index) => {
      const legendKey = getLegendKey(item);
      const paletteIndex = legendKeyToPaletteIndex[legendKey] ?? index;
      return findRgbaForLegend(
        legendKey,
        paletteIndex,
        chartConfig?.legendColors,
      );
    });
  }, [chartConfig?.legendColors, seriesData]);

  // 图表宽高变化时, 会重新计算得到新的浮窗宽高
  const tooltipFormatter = useCallback((params: Array<IEChartsTooltipParams>) => {
    const tooltipParams = Array.isArray(params) ? params : [params];
    const { showTotal = true } = tooltipSetting || {};
    // 排序，others排在最后
    const sortParams = orderBy(
      tooltipParams,
      [(item: { value: string; }) => (item?.value ? parseFloat(item?.value) : -Infinity)],
      ['desc']
    )?.sort((a, b) => {
      if (a?.seriesName === 'Others') return 1;
      if (b?.seriesName === 'Others') return -1;
      return 0;
    });
    const records: ITooltipRecord[] = [];
    const columns = [
      {
        key: 'first',
        header: getNullTextOfFilter(params[0]?.axisValueLabel),
        align: 'start',
      }
    ];
    metrics?.forEach(name => {
      columns?.push({
        key: name,
        header: isOnlyOneDimension ? '' : name?.replace(/(t[0-2]_)/, '') || '',
        align: 'end',
      });
    });
    sortParams?.forEach((item) => {
      const { data, marker, } = item || {};
      const { metricName, value, dimensions } = data || {};
      const name = metricName?.replace(/(t[0-2]_)/, '') || '';
      const col = {
        first: isOnlyOneDimension ? `${marker}${name}` : `${marker}${dimensions}`
      };
      const text = value ? `${value}%` : '-';
      col[metricName] = `<span class="dbl-new-chart-tooltip-body-cell-value" >${text || '-'}</span>`;
      records?.push(col);
    });

    // total汇总值展示，由tooltipSetting的showTotal控制
    const totalCol: ITooltipRecord = { first: formatMessage({ id: 'dbl_chart_tooltip_total' }) };
    // 计算 value 累加值（处理字符串转数字，兼容异常值）
    const totalValue = tooltipParams?.reduce((sum, item) => {
      // 将 value 从字符串转为数字，转换失败则取 0
      const numValue = Number(item?.data?.oPercent) || 0;
      // 累加（避免浮点数精度问题）
      return Math.round((sum + numValue) * 1000) / 1000;
    }, 0);
    const totalText = (Math.round((totalValue) * 100) / 100) >= 100 ? '100%' : `${totalValue?.toFixed(2)}%`;
    totalCol[metrics?.[0] || ''] = `<span class="dbl-new-chart-tooltip-body-cell-value" >${totalText}</span>`;
    const newRecords = showTotal ? [totalCol, ...(records || [])] : records;
    return generateTooltipGrid(columns, newRecords, {
      height: (chart?.current?.clientHeight || 379) + (showTotal ? 30 : 0),
      width: chart?.current?.clientWidth || 400,
    });
  }, [chart, formatMessage, isOnlyOneDimension, metrics, tooltipSetting]);

  const option = useBaseOptions({
    series: percentSeries,
    legendData,
    xAxisData,
    chartConfig,
    tooltipFormatter,
    setOtherConfigFunc: (opt: ChartOption) => {
      let option = { ...opt };
      option.color = seriesColors;
      if (option?.yAxis && Array.isArray(option.yAxis) && option.yAxis[0]) {
        option.yAxis[0].max = 100;
        if (option?.yAxis?.[0]?.axisLabel?.formatter) {
          option.yAxis[0].axisLabel.formatter = (value: number) => `${value}%`;
        }
      }
      option = setDataLabel(option, chartConfig, {
        label: {
          position: 'insideTop',
        },
        metricFormatter: (params) => {
          const isPercent = params?.data?.isPercent;
          const metricValue = params?.data?.metricValue ? formatNumberByComma(params?.data?.metricValue) : '-';
          return isPercent ? `${metricValue}%` : metricValue;
        }
      });
      // 设置x轴label展示
      option = setXLabelAlign(option, { type: xLabel });
      // 设置图例位置
      changeLegendLocation({option, position: chartConfig?.legendPosition});
      // 设置tooltip最大尺寸
      option = setTooltipMaxSize(option, chartConfig, { 
        chartLayoutRect: {
          width: chart?.current?.clientWidth || 400,
          height: chart?.current?.clientHeight || 379,
        },
      });
      return option;
    }
  });

  return <div className="analysis-chart-container">
    <div
      className={classNames('dbl-charts-top-yAxis-text', {
        'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
      })}
    >
      <div className='dbh-text-exceeds-bubble-left'>
        <TextBubbles
          text={yAxisName}
        />
      </div>
    </div>
    <div className='analysis-chart-content'>
      <Chart ref={chartRef} option={option} notMerge={true} />
    </div>
  </div>;
};

const PercentStackBarChart = React.forwardRef(PercentStackBarChartInner);

export default PercentStackBarChart;